import { $, moment } from '../../lib/ext-refs';

/**
 * This function library implements CardConnect-specific behaviour which
 * is bound to generic UI components specific to card-present handling.
 * 
 * populateDeviceList(): populates the deviceList member (local state) of a 
 *      TerminalDeviceList, with the terminal devices available in a specific
 *      context.
 * 
 * invokeTerminalTxn(): invokes the appropriate API to start the SALE txn
 *      at the terminal. The results are used to populate the 'paymentResponse'
 *      object under 'data'.
 */


function populateDeviceList() {
    const {state} = this.props;
    const {data, config} = state;
    const {locationId, tla, messages} = config;
    const {nginxJwtToken} = data;
    let handleResponse = this.handleDeviceListResponse;
    let deviceList;

    // Will need to revisit when adding support for billers with a multipay configuration
    // using the Ingenico One integration with the default scheme which can require
    // the location ID, location qualifier, and the payment type code as a complete
    // context for resolving a set of devices available to target a location (merchant
    // account).
    const context = {
        "location-id": `${locationId}`
    };

    $.ajax({
        method: 'POST',
        url: `/v1/location/devices/list/${tla}`,
        headers: {Authorization: `Bearer ${nginxJwtToken}`},
        data: context,
        dataType: "json",
        success: (data) => {
            if (data.terminals) {
                deviceList = data.terminals.map(item => ({ code: item[1], description: item[0] }))
                    .sort((item1, item2) => (item1.description.toUpperCase() > item2.description.toUpperCase()) ? 1 : -1);
            } else {
                deviceList = [];
            }
        },
        error: (jqXHR, textStatus, errorThrown) => {
            console.log('Error: ', jqXHR, textStatus, errorThrown);
        },
        complete: () => {
            handleResponse(deviceList);
        }
    });
}

function invokeTerminalTxn() {
    const {state} = this.props;
    const {data, config, setValues} = state;
    const {locationId, messages, tla} = config;
    const {nginxJwtToken} = data;
    const STATUS_CHECK_INTERVAL_MILLIS = 1000;
    const STATUS_CHECK_TIMEOUT_SECONDS = config.paymentConfig.cardPresentStatusCheckTimeoutSeconds;
    const handleResponse = this.handleCardPresentResponse.bind(this);
    const GENERIC_ERROR = messages["card.present.generic.authcapture.failure"];

    try {
        let saleTxn = {
            "location-id": `${locationId}`,
            "hsn": `${data.paymentMethod.cardPresentTerminalHSN}`,
            "payment-ref": `${data.paymentResponse.referenceNumber}`,
            "manual-entry": `${data.paymentMethod.cardPresentManualEntry}`,
            "capture-signature": `${config.paymentConfig.cardPresentCaptureSignature}`
        };

        // Initiate a SALE txn and then poll for a finalized Paymentus status or
        // wait for a timeout before moving the flow along
        $.ajax({
            method: 'POST',
            url: `/v1/cardpresent/make-payment/${tla}`,
            headers: {Authorization: `Bearer ${nginxJwtToken}`},
            data: saleTxn,
            dataType: 'json',
            success: (result) => {
                const statusCheckStart = moment();
                let timer = setTimeout(function checkStatus() {
                    $.ajax({
                        url: `./xotp-api/payments/ref/${tla}/${saleTxn["payment-ref"]}`,
                        headers: {Authorization: `Bearer ${nginxJwtToken}`},
                        dataType: 'json',
                        success: (result) => {
                            const elapsedSeconds = moment.duration(moment().diff(statusCheckStart)).asSeconds()
                            const response = result["last-payment-response"];
                            const status = response["payment-status"];
                            const finalStatus = response["payment-status-final"];
                            const statusDescription = response["payment-status-description"];
                            const pmType = response["payment-method-type"];
                            const cardNumber = response["masked-account-number"];
                            const cardEntryMode = response["payment-method-card-entry-mode"];
                            const aid = response["payment-method-aid"];
                            const tvr = response["payment-method-tvr"];
                            const tsi = response["payment-method-tsi"];
                            const arc = response["payment-method-arc"];
                            const iad = response["payment-method-iad"];
                            const authMode = response["payment-method-auth-mode"];
                            if (finalStatus || elapsedSeconds > STATUS_CHECK_TIMEOUT_SECONDS) {
                                clearTimeout(timer);
                                if (elapsedSeconds > STATUS_CHECK_TIMEOUT_SECONDS) {
                                    setValues((data) => {
                                        data.paymentResponse.paymentStatus = "TIMEOUT";
                                        return data;
                                    });
                                } else {
                                    setValues((data) => {
                                        data.paymentResponse.paymentStatus = status;
                                        data.paymentResponse.paymentStatusDescription = statusDescription;
                                        data.paymentMethod.type = pmType;
                                        data.paymentMethod.accountNumber = cardNumber;
                                        data.paymentMethod.cardEntryMode = cardEntryMode;
                                        data.paymentMethod.aid = aid;
                                        data.paymentMethod.tvr = tvr;
                                        data.paymentMethod.tsi = tsi;
                                        data.paymentMethod.arc = arc;
                                        data.paymentMethod.iad = iad;
                                        data.paymentMethod.authMode = authMode;
                                        return data;
                                    });
                                }
                                handleResponse();
                                return;
                            }
                            timer = setTimeout(checkStatus, STATUS_CHECK_INTERVAL_MILLIS);
                        },
                        error: (result) => {
                            clearTimeout(timer);
                            setValues((data) => {
                                data.paymentResponse.paymentStatus = "TIMEOUT";
                                return data;
                            });
                            handleResponse();
                        }
                    })
                }, STATUS_CHECK_INTERVAL_MILLIS);
            },
            error: (result) => {
                setValues((data) => {
                    data.paymentResponse.paymentStatus = "TIMEOUT";
                    return data;
                });
                handleResponse();
            }
        })
    }
    catch (ex) {
        setValues((data) => {
            const error = ex.message ? ex.message : GENERIC_ERROR;
            console.log(`Exception while invoking transaction: ${error}`);
            data.paymentResponse.paymentStatus = "TIMEOUT";
            data.paymentResponse.paymentStatusDescription = error;
        });
        handleResponse();
    }
}

export { invokeTerminalTxn, populateDeviceList };